<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import BrowseStrategiesFilters from './BrowseStrategiesFilters.vue'
import StrategyImageTile from './StrategyImageTile.vue'
import { getStrategies } from '@/strategies/api'
import { useUserStore } from '@/authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { selectionFromQuery, selectionToQuery } from './strategy_filters'
import type { StrategyFilterSelection, StrategySummary } from './strategy_types'

const MAX_UNLOCKED_FOR_NON_PREMIUM = 13
const KEYWORD_SEARCH_RESULT_SIZE = 150
const BROWSE_RESULT_SIZE = 400
const EMPTY_RESULTS_TEXT = 'No strategies found for the given criteria.'

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const { isPremium } = storeToRefs(useUserStore())

const strategies = ref<StrategySummary[]>([])
// The filters the rendered tiles were fetched with. selection follows the route, which
// has already moved on while a replacement search is in flight, so a click during that
// window would otherwise attribute a tile from the visible search to the pending one.
const renderedSelection = ref<StrategyFilterSelection>(selectionFromQuery({}))
const fetchingStrategies = ref(true)
const errorMessage = ref('')

let inFlightRequest: AbortController | null = null

const selection = computed(() => selectionFromQuery(route.query))

const resultStatus = computed(() => {
  if (fetchingStrategies.value) {
    return 'Loading...'
  }
  if (!strategies.value.length) {
    return ''
  }
  return `${strategies.value.length} ${strategies.value.length === 1 ? 'result' : 'results'}`
})

function filtersEventData(from: StrategyFilterSelection = selection.value) {
  return {
    subject: from.subject || undefined,
    barrier: from.barrier || undefined,
    grades: from.grades || undefined,
    setting: from.setting || undefined,
    teachingType: from.teachingType || undefined,
    query: from.query || undefined
  }
}

function withAccessSortedFirst(results: StrategySummary[]) {
  if (isPremium.value) {
    return results
  }
  // non-premium users see unlocked strategies first, ahead of keyword relevance,
  // and only the first 13 of them are unlocked
  return [...results]
    .sort((a, b) => Number(Boolean(b.demo)) - Number(Boolean(a.demo)))
    .map((strategy, index) => ({
      ...strategy,
      demo: index < MAX_UNLOCKED_FOR_NON_PREMIUM && strategy.demo
    }))
}

async function loadStrategies() {
  inFlightRequest?.abort()
  inFlightRequest = new AbortController()
  const request = inFlightRequest

  fetchingStrategies.value = true
  errorMessage.value = ''

  try {
    const results = await getStrategies(
      {
        tags: [selection.value.subject, selection.value.barrier].filter(Boolean).join(','),
        setting: selection.value.setting || undefined,
        teachingType: selection.value.teachingType || undefined,
        grades: selection.value.grades?.join(',') || undefined,
        query: selection.value.query || undefined,
        size: selection.value.query ? KEYWORD_SEARCH_RESULT_SIZE : BROWSE_RESULT_SIZE
      },
      request.signal
    )
    if (request.signal.aborted) {
      return
    }
    strategies.value = withAccessSortedFirst(results)
    renderedSelection.value = selection.value
    analytics.sendEvent(EVENT_NAMES.BROWSE_STRATEGIES.FILTERED_RESULTS, filtersEventData())
  } catch {
    if (request.signal.aborted) {
      return
    }
    strategies.value = []
    errorMessage.value = 'Error retrieving strategies. Please reload the page to try again.'
  } finally {
    if (!request.signal.aborted) {
      fetchingStrategies.value = false
    }
  }
}

function updateFilter({ field, value }: { field: keyof StrategyFilterSelection; value: string | string[] | null }) {
  const nextSelection = { ...selection.value, [field]: value } as StrategyFilterSelection
  if (field === 'subject') {
    nextSelection.barrier = ''
  }
  // route.query is passed so the ?beta=true opt-in survives the navigation; the
  // analytics payload below deliberately does not carry it
  router.push({ name: 'BrowseStrategiesPage', query: selectionToQuery(nextSelection, route.query) })
}

function strategyClicked(strategy: StrategySummary) {
  // Vue 3's tracker takes no per-event metadata argument, so the content uuid and
  // title travel in the event data the way the rest of this SPA sends them.
  analytics.sendEvent(EVENT_NAMES.BROWSE_STRATEGIES.CLICKED_RESULT, {
    uuid: strategy.uuid,
    title: strategy.title,
    cu: strategy.uuid,
    ct: strategy.title,
    ...filtersEventData(renderedSelection.value)
  })
}

watch(() => route.query, loadStrategies)

onUnmounted(() => inFlightRequest?.abort())

loadStrategies()

analytics.sendEvent(EVENT_NAMES.BROWSE_STRATEGIES.VIEWED_PAGE, {
  ...filtersEventData(),
  ref: route.query.ref
})
</script>

<template>
  <div class="container min-h-screen">
    <header>
      <h1 class="header-xl mb-9 mt-8 text-center font-regular text-gray-900">Browse Strategies</h1>
      <BrowseStrategiesFilters :selection="selection" @update:filter="updateFilter" />
    </header>

    <div>
      <p v-if="errorMessage" role="alert" class="text-center text-text-xl text-gray-900">{{ errorMessage }}</p>
      <p v-else-if="resultStatus" role="status" class="text-text-lg text-gray-700">{{ resultStatus }}</p>

      <p
        v-if="!fetchingStrategies && !errorMessage && strategies.length === 0"
        role="status"
        class="mt-8 text-center text-text-2xl text-gray-700"
      >
        {{ EMPTY_RESULTS_TEXT }}
      </p>

      <section :class="{ 'opacity-[0.55]': fetchingStrategies }">
        <ul class="grid list-none grid-cols-1 gap-x-6 sm:grid-cols-2 md:grid-cols-3">
          <li v-for="strategy of strategies" :key="strategy.uuid">
            <StrategyImageTile :strategy="strategy" @click="strategyClicked(strategy)" />
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
